import { useState, useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSiteImportUIStore } from '../stores/siteImportUIStore';
import {
  previewSiteImport,
  importSite,
  SiteImportPreviewResponse,
  SiteImportResponse,
} from '../api/sitesApi';
import { SiteImportFormData } from '../schemas/siteImportSchema';
import { TradeTemplate } from '../constants/tradeTemplates';
import { sitesKeys } from './useSites';
import { onboardingKeys } from '../../onboarding/hooks/onboardingKeys';
import { Routes } from '../../../routes';

export function useSiteImportFlow(form: UseFormReturn<SiteImportFormData>) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { nextStep, resetNavState } = useSiteImportUIStore();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] =
    useState<SiteImportPreviewResponse | null>(null);
  const [importResult, setImportResult] = useState<SiteImportResponse | null>(
    null
  );
  const [templateFetchError, setTemplateFetchError] = useState<string | null>(
    null
  );
  const [allLabourItemsResolved, setAllLabourItemsResolved] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );
  const [selectedTemplate, setSelectedTemplate] =
    useState<TradeTemplate | null>(null);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => previewSiteImport(file),
    onSuccess: data => {
      setPreviewData(data);
      setAllLabourItemsResolved(false);
      nextStep();
    },
  });

  const importMutation = useMutation({
    mutationFn: importSite,
    onSuccess: async data => {
      setImportResult(data);
      await qc.invalidateQueries({ queryKey: sitesKeys.all });
      await qc.invalidateQueries({ queryKey: onboardingKeys.status() });
      nextStep();
    },
  });

  const handleSelectTemplate = useCallback((template: TradeTemplate) => {
    setSelectedTemplateId(template.id);
    setSelectedTemplate(template);
  }, []);

  const handleTemplateConfirm = useCallback(async () => {
    if (!selectedTemplate) return;
    setTemplateFetchError(null);
    try {
      const response = await fetch(selectedTemplate.csvPath);
      if (!response.ok) {
        setTemplateFetchError('Failed to load template. Please try again.');
        return;
      }
      const blob = await response.blob();
      const file = new File([blob], selectedTemplate.fileName, {
        type: 'text/csv',
      });
      setSelectedFile(file);
      uploadMutation.mutate(file);
    } catch {
      setTemplateFetchError('Failed to load template. Please try again.');
    }
  }, [selectedTemplate, uploadMutation]);

  const handleFileSelected = (file: File) => {
    setSelectedFile(file);
    uploadMutation.mutate(file);
  };

  const handleImport = () => {
    if (!previewData) return;
    const { name, slug, notes, what3words, latitude, longitude } =
      form.getValues();
    importMutation.mutate({
      siteName: name,
      slug: slug.toUpperCase(),
      notes: notes || undefined,
      what3WordsAddress: what3words || '',
      latitude,
      longitude,
      plots: previewData.plots,
    });
  };

  const handleConfirmCancel = () => {
    form.reset();
    setPreviewData(null);
    setImportResult(null);
    setSelectedFile(null);
    setAllLabourItemsResolved(false);
    setTemplateFetchError(null);
    resetNavState();
    navigate(Routes.sites.manage);
  };

  return {
    selectedFile,
    previewData,
    importResult,
    templateFetchError,
    allLabourItemsResolved,
    setAllLabourItemsResolved,
    selectedTemplateId,
    uploading: uploadMutation.isPending,
    importing: importMutation.isPending,
    uploadError: uploadMutation.isError
      ? 'Failed to process file. Please try again.'
      : null,
    handleSelectTemplate,
    handleTemplateConfirm,
    handleFileSelected,
    handleImport,
    handleConfirmCancel,
  };
}
